/**
 * Centralized Error Handling for Frontend
 * Prevents duplicate toasts and provides consistent error messages
 */

import { toast } from '@/hooks/use-toast'

// Track recent toasts to prevent duplicates
const recentToasts = new Map<string, number>()
const TOAST_COOLDOWN = 3000 // 3 seconds

/**
 * Error codes from backend
 */
export enum ErrorCode {
  UNAUTHORIZED = 'UNAUTHORIZED',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
  INVALID_LEVERAGE = 'INVALID_LEVERAGE',
  INVALID_ORDER_SIZE = 'INVALID_ORDER_SIZE',
  INSTRUMENT_NOT_FOUND = 'INSTRUMENT_NOT_FOUND',
  MARKET_CLOSED = 'MARKET_CLOSED',
  POSITION_NOT_FOUND = 'POSITION_NOT_FOUND',
  ORDER_REJECTED = 'ORDER_REJECTED',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  INVALID_INPUT = 'INVALID_INPUT',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  TIMEOUT = 'TIMEOUT',
  BROKER_ERROR = 'BROKER_ERROR',
  LLM_ERROR = 'LLM_ERROR',
}

/**
 * Check if we should show a toast (prevents duplicates)
 */
function shouldShowToast(key: string): boolean {
  const now = Date.now()
  const lastShown = recentToasts.get(key)
  
  if (lastShown && now - lastShown < TOAST_COOLDOWN) {
    return false
  }
  
  recentToasts.set(key, now)
  
  // Clean up old entries
  if (recentToasts.size > 50) {
    const cutoff = now - TOAST_COOLDOWN
    for (const [k, v] of recentToasts.entries()) {
      if (v < cutoff) {
        recentToasts.delete(k)
      }
    }
  }
  
  return true
}

/**
 * Extract error message from various error formats
 */
function extractErrorMessage(error: any): { message: string; code?: string } {
  // API error response — backend returns { error: { code, message } }
  if (error.response?.data) {
    const data = error.response.data
    const errField = data.error
    if (errField && typeof errField === 'object') {
      return {
        message: errField.message || errField.details || 'Request failed',
        code: errField.code,
      }
    }
    return {
      message: typeof errField === 'string' ? errField : (data.message || 'Request failed'),
      code: data.code,
    }
  }
  
  // Direct error object with nested error field
  if (error.error && typeof error.error === 'object') {
    return {
      message: error.error.message || error.error.details || 'An error occurred',
      code: error.error.code || error.code,
    }
  }

  // Direct error object with string error field
  if (error.error && typeof error.error === 'string') {
    return { message: error.error, code: error.code }
  }
  
  // Standard Error object
  if (error.message) {
    return { message: error.message, code: error.code }
  }
  
  // String error
  if (typeof error === 'string') {
    return { message: error }
  }
  
  return { message: 'An unexpected error occurred' }
}

/**
 * Get toast variant based on error code
 */
function getToastVariant(code?: string): 'default' | 'destructive' | 'warning' {
  if (!code) return 'destructive'
  
  // Warning-level errors
  if ([
    ErrorCode.RATE_LIMIT_EXCEEDED,
    ErrorCode.MARKET_CLOSED,
    ErrorCode.SERVICE_UNAVAILABLE,
  ].includes(code as ErrorCode)) {
    return 'warning'
  }
  
  // Destructive errors
  return 'destructive'
}

/**
 * Handle error and show appropriate toast
 */
export function handleError(error: any, context?: string): void {
  const { message, code } = extractErrorMessage(error)
  const toastKey = `${code || 'error'}:${message}`
  
  // Check if we should show this toast
  if (!shouldShowToast(toastKey)) {
    console.log('Suppressing duplicate toast:', message)
    return
  }
  
  // Log to console for debugging
  console.error(`[${context || 'Error'}]`, error)
  
  // Show toast
  const variant = getToastVariant(code)
  toast({
    title: context || 'Error',
    description: message,
    variant,
  })
}

/**
 * Handle success message (also prevents duplicates)
 */
export function handleSuccess(message: string, title?: string): void {
  const toastKey = `success:${message}`
  
  if (!shouldShowToast(toastKey)) {
    return
  }
  
  toast({
    title: title || 'Success',
    description: message,
    variant: 'success',
  })
}

/**
 * Handle info message (also prevents duplicates)
 */
export function handleInfo(message: string, title?: string): void {
  const toastKey = `info:${message}`
  
  if (!shouldShowToast(toastKey)) {
    return
  }
  
  toast({
    title: title || 'Info',
    description: message,
    variant: 'info',
  })
}

/**
 * Handle warning message (also prevents duplicates)
 */
export function handleWarning(message: string, title?: string): void {
  const toastKey = `warning:${message}`
  
  if (!shouldShowToast(toastKey)) {
    return
  }
  
  toast({
    title: title || 'Warning',
    description: message,
    variant: 'warning',
  })
}

/**
 * Clear toast cooldown for a specific message (force show)
 */
export function clearToastCooldown(key: string): void {
  recentToasts.delete(key)
}

/**
 * Clear all toast cooldowns
 */
export function clearAllToastCooldowns(): void {
  recentToasts.clear()
}
